// DiagramBpmnIndex.tsx - FIXED VERSION
import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import DiagramBpmn from './DiagramBpmn';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import SidebarNavigation from './common/CustomDiagramBpnm';
import DataSourcesPage from '../EnterpriseContext/DataSourcePage';
import { closeDiagram, refreshTaxonomy, updateDiagramData } from './store/diagramViewerSlice';
import Navbar from '../../common/components/Navbar/Navbar';
import { API_BASE, API_GRAPH_BASE, ENVIRONMENT_TYPE } from '../../config/env';
import { httpService } from '../../utility/httpService';
import { httpConstants } from '../../constants';
import CustomProcessInfo from './common/CustomProcessInfo';
import ShowToast, { ToastTypeEnum } from '../../common/components/ShowToast';
import { useRegenerateSolutions } from '../../customHooks/useRegenerateSolutions';
import AiOpportunities from '../NewIdeationXPLR/modules/Opportunity/IdeationOpportunities/aiOpportunity';
import { useDiagramDeepLinkLoader } from './hooks/useDiagramDeepLinkLoader';
import OpportunitiesStep from '../TaxonomyXPLR/shared/components/OpportunitiesStep';
import { KPIsData } from '../NewIdeationXPLR/modules/Opportunity/IdeationOpportunities/processDetails';
import { OpportunityItem } from './store/diagramViewerInterface';

interface Opportunity {
  id: string;
  name: string;
  description: string;
  shape_id: string;
  valid?: boolean;
}

interface OpportunityStep {
  id?: string;
  opportunity_title: string;
  opportunity_description: string;
  affected_process_stages: string[];
  expected_business_value: string[];
}

interface ApiResponse<T = any> {
  success?: boolean;
  status?: number;
  error?: string;
  data?: T;
}

export interface CustomOpportunity {
  id?: string;
  opportunity_title: string;
  opportunity_description: string;
  affected_process_stages: string[];
  expected_business_value: string[];
}

const DiagramBpmnIndex: React.FC = () => {
  const dispatch = useDispatch();
  const { diagramData } = useSelector((state: RootState) => state.diagramViewer);
  const {
    DiagramData,
    aiOpportunityData,
    selectedTaxonomy,
    selectedE2E,
    selectedSubprocess,
    selectedCard,
    isEditPermission,
    selectedStage,
    selectedSolution,
    selectedSolutionDescription,
    taxonomySubprocessName,
    taxonomySubprocessDescription,
    useCaseData,
    useCaseDiagramDetails,
    tedStage,
  } = diagramData || {};

  const [activeSection, setActiveSection] = useState('process-diagram');
  const [customOpportunities, setCustomOpportunities] = useState<CustomOpportunity[]>(
    (selectedCard?.opportunity as { items?: CustomOpportunity[] })?.items || []
  );
  const [changesSavedCount, setChangesSavedCount] = useState(0);
  const [diagramSavedCount, setDiagramSavedCount] = useState(0);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [solutionsRefreshKey, setSolutionsRefreshKey] = useState(0);

  // Initialize diagram state when entering via /diagram?diagramId=...
  const { isLoadingDeepLink } = useDiagramDeepLinkLoader({ diagramData });

  // Track saved diagram XML to prevent unnecessary re-renders
  const savedDiagramXmlRef = useRef<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const { startPolling, fetchOpportunitiesAndSync } = useRegenerateSolutions();

  const isCustomProcess =
    selectedSubprocess?.processType === 'custom' || selectedCard?.processType === 'custom';

  const opportunities: Opportunity[] = Array.isArray(
    (aiOpportunityData as { opportunities?: Opportunity[] })?.opportunities
  )
    ? (aiOpportunityData as { opportunities: Opportunity[] }).opportunities
    : Array.isArray(aiOpportunityData)
      ? (aiOpportunityData as Opportunity[])
      : [];

  const handleNavigate = (section: string) => setActiveSection(section);
  const handleBack = () => {
    dispatch(closeDiagram());
    dispatch(refreshTaxonomy(true));
  };
  const currentOrg = localStorage.getItem('current_organization') || 'Empty';

  // checkStatus is now handled inside useRegenerateSolutions
  const fetchOpportunities = useCallback(
    async (showToast: boolean = false) => {
      try {
        // Use fetchOpportunitiesAndSync from the hook to get opportunities
        const opportunities = await fetchOpportunitiesAndSync(
          DiagramData?.diagram?.id,
          diagramData,
          false
        );

        // Map and store the opportunities locally and in Redux
        if (Array.isArray(opportunities) && opportunities.length > 0) {
          const mappedOpportunities: OpportunityItem[] = opportunities.map(
            (item): OpportunityItem => ({
              id: String(item.id),
              name: item.opportunity_title || 'Untitled Opportunity',
              description: item.opportunity_description || 'No description available',
              shape_id: item.shape_id || '',

              affected_process_stages: Array.isArray(item.affected_process_stages)
                ? item.affected_process_stages.join(', ')
                : '',
              expected_business_value: Array.isArray(item.expected_business_value)
                ? item.expected_business_value.join(', ')
                : '',
            })
          );
          // setCustomOpportunities(mappedOpportunities);
          dispatch(
            updateDiagramData({
              ...diagramData,
              custom_opportunities: mappedOpportunities,
            })
          );
          if (showToast) {
            ShowToast({
              message: 'Opportunities refreshed successfully',
              type: ToastTypeEnum.Success,
            });
          }
        } else {
          // setCustomOpportunities([]);
          dispatch(
            updateDiagramData({
              ...diagramData,
              custom_opportunities: [],
            })
          );
        }
      } catch (error) {
        console.error('Error fetching opportunities:', error);
        if (showToast) {
          ShowToast({
            message: 'Failed to fetch opportunities',
            type: ToastTypeEnum.Error,
          });
        }
      }
    },
    [DiagramData?.diagram?.id, diagramData, dispatch, fetchOpportunitiesAndSync]
  );

  const startPollingLocal = useCallback(
    (id: string) => {
      startPolling(id, async () => {
        await fetchOpportunities(true);
        setSolutionsRefreshKey((prev) => prev + 1);
        setChangesSavedCount(0);
        setDiagramSavedCount(0);
        setIsRegenerating(false);
        dispatch(
          updateDiagramData({
            ...diagramData,
            isRegenerating: false,
            regeneratingSubprocessId: undefined,
          })
        );
      });
    },
    [startPolling, fetchOpportunities, dispatch, diagramData]
  );

  const handleRegenerate = async () => {
    if (!selectedCard?.id || !DiagramData?.diagram?.id || !DiagramData?.diagram?.xml) {
      ShowToast({
        message: 'Missing required data for regeneration',
        type: ToastTypeEnum.Error,
      });
      return;
    }

    try {
      setIsRegenerating(true);
      // Expose regenerating state globally so TaxonomyXPLR can show overlay immediately
      dispatch(
        updateDiagramData({
          ...diagramData,
          isRegenerating: true,
          regeneratingSubprocessId: selectedCard.id,
        })
      );

      const statusUpdatePayload = {
        status: 'aisolutions_in_progress',
      };
      console.log('Updating status to in_progress for ID:', selectedCard);
      await httpService(
        httpConstants.METHOD_TYPE.POST,
        `${API_BASE}/api/v1/e2e_taxonomy/level2/${selectedCard.id}/status/`,
        statusUpdatePayload
      );

      console.log('Status updated to in_progress');

      const regeneratePayload = {
        name: selectedCard.name,
        diagram: {
          xmlImage: DiagramData.diagram.xml,
          id: DiagramData.diagram.id,
          nodeDescription: DiagramData.diagram.data || {},
          xmlData: '',
        },
        process_id: selectedCard.id,
        solution_id: selectedCard.id,
        opportunities: customOpportunities,
        description: taxonomySubprocessDescription || '',
        allProcesses: '',
        targetProcess: `${selectedE2E?.name || ''} -> ${selectedSubprocess?.name || ''} -> ${selectedCard?.name || ''}`,
        environment_type: ENVIRONMENT_TYPE,
        kpis: selectedCard.kpis || [],
      };

      console.log('Triggering ideation-to-diagram BPMN API with payload:', regeneratePayload);

      httpService(
        httpConstants.METHOD_TYPE.POST,
        `${API_BASE}/api/v1/e2e_taxonomy/orchestrator/ideation-to-diagram/bpmn/`,
        regeneratePayload
      )
        .then((response) => {
          console.log('Ideation-to-diagram BPMN API completed:', response);
        })
        .catch((error) => {
          console.error('Ideation-to-diagram BPMN API error:', error);
        });
      startPollingLocal(selectedCard.id);
      ShowToast({
        message: 'Regenerating opportunities',
        type: ToastTypeEnum.Success,
      });
    } catch (error) {
      console.error('Error regenerating opportunities:', error);
      ShowToast({
        message: 'Failed to regenerate opportunities',
        type: ToastTypeEnum.Error,
      });
      setIsRegenerating(false);
      dispatch(
        updateDiagramData({
          ...diagramData,
          isRegenerating: false,
          regeneratingSubprocessId: undefined,
        })
      );
    }
  };

  const handleDiagramSaved = (newXml: string, newData: any) => {
    console.log('Diagram saved - updating Redux state');
    savedDiagramXmlRef.current = newXml;
    setDiagramSavedCount((prev) => prev + 1);

    // CRITICAL: Update Redux store immediately after save
    dispatch(
      updateDiagramData({
        ...diagramData,
        DiagramData: {
          ...DiagramData,
          diagram: {
            ...DiagramData?.diagram!,
            xml: newXml,
            data: newData,
          },
        },
      })
    );
  };
  useEffect(() => {
    if (isCustomProcess && DiagramData?.diagram?.id) {
      fetchOpportunities();
    }
  }, [isCustomProcess, DiagramData?.diagram?.id]);

  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  const handleUpdateKPIs = async (process_description: string, kpis: any) => {
    const url = `${API_GRAPH_BASE}/custom-process/subprocess/${selectedCard?.id}`;
    try {
      const response = await httpService<any>(httpConstants.METHOD_TYPE.PUT, url, {
        process_description: process_description,
        kpis: kpis,
      });

      if (response) {
        dispatch(
          updateDiagramData({
            ...diagramData,
            selectedCard: {
              ...selectedCard,
              kpis: kpis,
            },
            taxonomySubprocessDescription: process_description,
          })
        );

        setChangesSavedCount((prev) => prev + 1);
      }
      return !!response;
    } catch (error) {
      console.error('KPI update failed:', error);
      return false;
    }
  };

  const handleRemove = async (opportunity: OpportunityStep, index: number) => {
    setCustomOpportunities((prev) => prev.filter((_, i) => i !== index));
    try {
      const response: ApiResponse = await httpService(
        httpConstants.METHOD_TYPE.DELETE,
        `${API_GRAPH_BASE}/api/v1/e2e_taxonomy/opportunities/${opportunity.id}`
      );

      if (response && response.success) {
        ShowToast({
          type: ToastTypeEnum.Success,
          message: 'Opportunity deleted successfully',
        });
      }
    } catch (error) {
      ShowToast({
        type: ToastTypeEnum.Error,
        message: 'Failed to delete opportunity',
      });
    }
  };

  const handleUpdateOpportunities = async (updatedOpportunities: any[]) => {
    if (!selectedCard?.id) return false;

    const url = `${API_GRAPH_BASE}/custom-process/subprocess/${selectedCard.id}`;

    try {
      const payload = {
        opportunity: {
          items: updatedOpportunities,
        },
      };

      const response = await httpService<any>(httpConstants.METHOD_TYPE.PATCH, url, payload);

      if (response) {
        dispatch(
          updateDiagramData({
            ...diagramData,
            selectedCard: {
              ...selectedCard,
              opportunities: {
                ...(selectedCard.opportunities as object),
                items: updatedOpportunities,
              },
            },
          })
        );

        setChangesSavedCount((prev) => prev + 1);
      }

      ShowToast({
        type: ToastTypeEnum.Success,
        message: 'Opportunities updated successfully',
      });

      return !!response;
    } catch (error) {
      console.error('Opportunity update failed:', error);
      ShowToast({
        type: ToastTypeEnum.Error,
        message: 'Failed to update opportunities',
      });
      return false;
    }
  };

  if (isLoadingDeepLink) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">
          Loading diagram…
        </Typography>
      </Box>
    );
  }

  if (!DiagramData) {
    return <Box sx={{ p: 4, textAlign: 'center' }}>No diagram data to display.</Box>;
  }

  if (isCustomProcess) {
    return (
      <Box display="flex" flexDirection="column" height="100vh">
        <Navbar />

        <Box display="flex" flex={1} overflow="hidden" mt="0px">
          <Box sx={{ width: 280, borderRight: '1px solid #E0E0E0' }}>
            <SidebarNavigation
              onNavigate={handleNavigate}
              onBack={handleBack}
              onRegenerate={handleRegenerate}
              regenerateDisabled={
                activeSection === 'process-diagram'
                  ? diagramSavedCount === 0
                  : changesSavedCount === 0
              }
              regenerateDisabledReason={
                activeSection === 'process-diagram'
                  ? diagramSavedCount === 0
                    ? 'Edit and save diagram to enable regeneration'
                    : ''
                  : changesSavedCount === 0
                    ? 'Make changes and save to enable regeneration'
                    : ''
              }
              selectedCard={selectedCard}
            />
          </Box>

          <Box
            flex={1}
            p={3}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              scrollBehavior: 'smooth',
              backgroundColor: '#FFFFFF',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            <Box flex={1} sx={{ minHeight: 0, pt: '12px' }}>
              {activeSection === 'process-diagram' && (
                <DiagramBpmn
                  key={`diagram-${diagramSavedCount}`}
                  diagramData={DiagramData}
                  aiOpportunityData={opportunities}
                  // isEditPermission={isEditPermission}
                  selectedTaxonomy={selectedTaxonomy}
                  selectedE2E={selectedE2E}
                  selectedSubprocess={selectedSubprocess}
                  selectedCard={selectedCard}
                  selectedSolution={selectedSolution}
                  selectedSolutionDescription={selectedSolutionDescription}
                  taxonomySubprocessName={taxonomySubprocessName}
                  taxonomySubprocessDescription={taxonomySubprocessDescription}
                  selectedStage={selectedStage || tedStage}
                  useCaseData={useCaseData}
                  useCaseDiagramDetails={useCaseDiagramDetails}
                  onClose={handleBack}
                  onResetAll={handleBack}
                  onResetTaxonomy={handleBack}
                  onResetE2E={handleBack}
                  kpis={selectedCard?.kpis}
                  onDiagramSaved={handleDiagramSaved}
                />
              )}

              {activeSection === 'process-info' && (
                <CustomProcessInfo
                  processDescription={taxonomySubprocessDescription ?? ''}
                  kpis={selectedCard?.kpis as KPIsData}
                  files={selectedCard?.files}
                  onSaveStateChange={() => {}}
                  onUpdate={(description: string, updatedKpis: KPIsData) => {
                    handleUpdateKPIs(description, updatedKpis);
                  }}
                />
              )}

              {activeSection === 'opportunities' && (
                <OpportunitiesStep
                  opportunities={customOpportunities}
                  setOpportunities={setCustomOpportunities}
                  isMobile={false}
                  isEditMode={true}
                  showFooter={true}
                  handleRemove={handleRemove}
                  handleUpdateOpportunities={handleUpdateOpportunities}
                />
              )}

              {activeSection === 'solutions' && (
                <Box sx={{mt: 5}}>
                <AiOpportunities
                  diagramId={DiagramData?.diagram?.id as string}
                  selectedCard={selectedCard}
                  refreshKey={solutionsRefreshKey}
                  isLoading={isRegenerating}
                  isProcess={true}
                />
                </Box>
              )}

              {activeSection === 'data-sources' && (
                <DataSourcesPage
                  solution_id={((selectedSolution as Record<string, unknown>)?.id as string) || ''}
                  diagram_id={DiagramData?.diagram?.id as string}
                  organization_id={currentOrg || ''}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <DiagramBpmn
      diagramData={DiagramData}
      aiOpportunityData={opportunities}
      isEditPermission={isEditPermission}
      selectedTaxonomy={selectedTaxonomy}
      selectedE2E={selectedE2E}
      selectedSubprocess={selectedSubprocess}
      selectedCard={selectedCard}
      selectedSolution={selectedSolution}
      selectedSolutionDescription={selectedSolutionDescription}
      taxonomySubprocessName={taxonomySubprocessName}
      taxonomySubprocessDescription={taxonomySubprocessDescription}
      selectedStage={selectedStage || tedStage}
      useCaseData={useCaseData}
      useCaseDiagramDetails={useCaseDiagramDetails}
      onClose={handleBack}
      onResetAll={handleBack}
      onResetTaxonomy={handleBack}
      onResetE2E={handleBack}
      kpis={selectedCard?.kpis}
      onDiagramSaved={handleDiagramSaved}
    />
  );
};

export default DiagramBpmnIndex;
